/**
 * Portal family-host + path-tenant conventions (Phase 1).
 *
 * Short-term: family hosts `devices.portal…` / `gop.portal…` /
 * `partners.portal…` with tenant selected by SPA path `/t/<slug>/…`
 * and API header `X-Portal-Tenant`. Vanity hosts (`testra.devices.portal…`)
 * are Phase 2.
 *
 * See docs/ops/partner-portal-swa-bind.md.
 */
/** HTTP header the portal SPA sends on family hosts so API scopes the schema. */
export const PORTAL_TENANT_HEADER = "x-portal-tenant";
/** Canonical portal root used for newly generated customer URLs. */
export const CANONICAL_PORTAL_DOMAIN_ROOT = "portal.l30.solutions";
/**
 * Canonical portal API host — a Container Apps custom domain, not a
 * Static Web App host, because SWA strips `Set-Cookie` from backend
 * responses (Sprint A Day 12).
 */
export const CANONICAL_PORTAL_API_HOST = `api.${CANONICAL_PORTAL_DOMAIN_ROOT}`;
/** Canonical dev root, the pre-production twin of the canonical estate. */
export const CANONICAL_PORTAL_DEV_DOMAIN_ROOT = "portal.dev.l30.solutions";
/** Legacy production root, retired once every family CNAME has moved. */
export const LEGACY_PORTAL_DOMAIN_ROOT = "portal.latitude30.co";
/** Legacy dev root. Stays live through the migration and is never redirected. */
export const LEGACY_PORTAL_DEV_DOMAIN_ROOT = "portal.dev.latitude30.co";
/**
 * Roots accepted while the latitude30.co portal estate is migrated.
 * Every shape is listed so host parsing works without an
 * environment-supplied root (unit runs, local lab, direct API hits).
 */
export const PORTAL_DOMAIN_ROOTS = [
    CANONICAL_PORTAL_DOMAIN_ROOT,
    CANONICAL_PORTAL_DEV_DOMAIN_ROOT,
    LEGACY_PORTAL_DOMAIN_ROOT,
    LEGACY_PORTAL_DEV_DOMAIN_ROOT,
];
/**
 * Which legacy root each canonical root replaces. Pairing is per
 * environment so a dev bundle can never bounce a production visitor
 * onto the dev estate (or the reverse).
 */
export const PORTAL_ROOT_MIGRATION_PAIRS = [
    { legacy: LEGACY_PORTAL_DOMAIN_ROOT, canonical: CANONICAL_PORTAL_DOMAIN_ROOT },
    { legacy: LEGACY_PORTAL_DEV_DOMAIN_ROOT, canonical: CANONICAL_PORTAL_DEV_DOMAIN_ROOT },
];
/** Lowercase and strip stray leading/trailing dots from a configured root. */
export function normalisePortalDomainRoot(portalDomainRoot) {
    return portalDomainRoot.toLowerCase().replace(/^\.+|\.+$/g, "");
}
/** True only for the l30.solutions roots — i.e. this environment has cut over. */
export function isCanonicalPortalDomainRoot(portalDomainRoot) {
    if (!portalDomainRoot)
        return false;
    const root = normalisePortalDomainRoot(portalDomainRoot);
    return PORTAL_ROOT_MIGRATION_PAIRS.some((pair) => pair.canonical === root);
}
/** DNS labels that identify family shells rather than arbitrary tenant hosts. */
export const PORTAL_FAMILY_HOST_LABELS = ["devices", "gop", "partners"];
const GOP_TENANTS = new Set(["gop", "moip", "mocc"]);
const PARTNERS_TENANTS = new Set(["belsize"]);
const PORTAL_LABEL_PATTERN = /^[a-z0-9-]+$/;
export function isPortalFamilyHostLabel(label) {
    return PORTAL_FAMILY_HOST_LABELS.includes(label.toLowerCase());
}
/**
 * Exact browser origins for the family shells on one portal root.
 *
 * Only three hosts exist per root, so CORS can name them instead of
 * wildcarding the whole root — a wildcard would also admit any future
 * label someone points at the estate.
 */
export function portalFamilyOrigins(portalDomainRoot) {
    const root = normalisePortalDomainRoot(portalDomainRoot);
    return PORTAL_FAMILY_HOST_LABELS.map((family) => `https://${family}.${root}`);
}
/**
 * Family labels cannot identify leaf tenants. `gop` is the one migration
 * exception because it remains both the original tenant and its family shell.
 */
export function isReservedPortalLeafSlug(slug) {
    const normalised = slug.toLowerCase();
    return isPortalFamilyHostLabel(normalised) && normalised !== "gop";
}
/**
 * Map a tenant to its family. A persisted assignment supplied by the API wins;
 * the static cases keep URL construction safe before metadata lookup.
 */
export function familyForTenant(tenantSlug, assignedFamily) {
    if (assignedFamily)
        return assignedFamily;
    if (GOP_TENANTS.has(tenantSlug.toLowerCase()))
        return "gop";
    if (PARTNERS_TENANTS.has(tenantSlug.toLowerCase()))
        return "partners";
    return "devices";
}
/**
 * Family shell used in generated portal URLs on `portalDomainRoot`.
 *
 * All three family labels (`devices` / `gop` / `partners`) have a
 * dedicated host on each portal root. The root still selects which DNS
 * tree the URL sits on.
 */
export function portalHostFamily(family, _portalDomainRoot) {
    return family;
}
/**
 * Extract the single label immediately above an accepted portal root.
 * Exact suffix comparison prevents deep and attacker-controlled lookalikes.
 */
export function portalHostLabel(host, additionalRoots = []) {
    if (!host)
        return null;
    let hostname;
    try {
        const parsed = new URL(`http://${host}`);
        if (parsed.username ||
            parsed.password ||
            parsed.pathname !== "/" ||
            parsed.search ||
            parsed.hash) {
            return null;
        }
        hostname = parsed.hostname.toLowerCase();
    }
    catch {
        return null;
    }
    const roots = new Set([...PORTAL_DOMAIN_ROOTS, ...additionalRoots]);
    for (const rawRoot of roots) {
        const root = rawRoot.toLowerCase().replace(/^\.+|\.+$/g, "");
        const suffix = `.${root}`;
        if (!hostname.endsWith(suffix))
            continue;
        const label = hostname.slice(0, -suffix.length);
        return PORTAL_LABEL_PATTERN.test(label) ? label : null;
    }
    return null;
}
/**
 * Staff applications stay on the legacy portal estate until infrastructure
 * cutover. An environment-provided root always wins.
 */
export function resolveStaffPortalDomainRoot(args) {
    if (args.configuredRoot)
        return args.configuredRoot;
    return args.hostname?.includes(".dev.") || args.hostname?.includes("-dev.")
        ? "portal.dev.latitude30.co"
        : "portal.latitude30.co";
}
/**
 * Build the browser URL for a portal SPA path after magic-link verify.
 * Phase 1: `https://{family}.{portalRoot}/t/{tenant}{path}`
 * Legacy single-tenant host `gop` still uses `/t/gop/...` for consistency.
 *
 * `portalDomainRoot` is required: a default here would silently emit
 * hosts for whichever estate the constant happened to name.
 */
export function portalSpaUrl(args) {
    const portalDomainRoot = normalisePortalDomainRoot(args.portalDomainRoot);
    const family = portalHostFamily(familyForTenant(args.tenantSlug, args.family), portalDomainRoot);
    const path = args.path.startsWith("/") ? args.path : `/${args.path}`;
    return `https://${family}.${portalDomainRoot}/t/${args.tenantSlug.toLowerCase()}${path}`;
}
//# sourceMappingURL=portal-hosting.js.map